import { memo, useMemo } from 'react';

import { graphql } from '@apollo/client/react/hoc';
import to from 'await-to-js';
import gql from 'graphql-tag';
import compose from 'lodash.flowright';
import { BellRing, LinkIcon, PlusCircle, Settings, X, Zap } from 'lucide-react';
import pathToRegexp from 'path-to-regexp';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { Button } from '@/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/shadcn/dialog';
import { cn } from '@/shadcn/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { notify } from '@/components/toast';
import { AddToFavorite } from '@/components/ui/addToFavorite';
import { Ellipsis } from '@/components/ui/ellipsis/ellipsis';
import { env } from '@/constant/env';
import {
  NEW_WF_OVERVIEW_ROUTE,
  ROUTE_WORKFLOW_GROUPS,
  ROUTE_WORKFLOW_OVERVIEW
} from '@/constant/routes';
import { SpanType, withSpanAsync } from '@/tracing';
import { formatNumber } from '@/utils';
import DownloadFile from './downloadFile';
import SubscriptionsMenu from './subscriptionsMenu';
import mutation from './uploadDocumentationModal/mutation';
import UploadDocumentationModal from './uploadDocumentationModal/uploadDocumentationModal';

export const WorkflowRow = memo(
  ({
    workflow,
    isPrivileged = false,
    updateWorkflowDocumentation,
    userId,
    isFavorite,
    onFavoriteChange
  }) => {
    const {
      rowId,
      canUpdate,
      name,
      traces,
      groups,
      documentation,
      actions: { nodes: actions },
      settings
    } = workflow;

    const ff_fastWorkflow = useMemo(() => {
      const ff_fastWorkflow = localStorage.getItem('ff_fastWorkflow');
      return ff_fastWorkflow === 'true';
    }, []);

    const subscriptionsDisabled = settings?.disableSubscriptions === true;

    const deleteWorkflowDocumentation = () => {
      const { url, file } = documentation;
      let documentationName;

      if (url) documentationName = url;
      if (file) documentationName = file.name;

      withSpanAsync(
        'updateWorkflowDocumentation',
        SpanType.processing,
        async () => {
          const promise = updateWorkflowDocumentation({
            variables: { rowId, url: '', fileDigest: null }
          });

          notify.promise(promise, {
            loading: 'Deleting documentation...',
            success: `${documentationName} documentation deleted`,
            error: `${documentationName} documentation could not be deleted`
          });
          await to(promise);
        }
      );
    };

    const renderDeleteWorkflowDoc = () => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label="Click to delete documentation"
              data-cy="delete-button"
              onClick={deleteWorkflowDocumentation}
              size="icon"
              type="reset"
              variant="ghostDestructiveSubtle"
            >
              <X />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Delete documentation</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );

    const renderDocumentation = () => {
      if (documentation?.url) {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <LinkIcon size={16} />
                  <Ellipsis>
                    <a
                      className="hover:text-primary hover:underline"
                      href={documentation.url}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {documentation.url}
                    </a>
                  </Ellipsis>
                  {canUpdate && renderDeleteWorkflowDoc()}
                </div>
              </TooltipTrigger>
              <TooltipContent>Click to open documentation</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
      if (documentation?.file) {
        return (
          <div className="flex items-center gap-2 text-muted-foreground">
            <DownloadFile data={documentation.file} />
            {canUpdate && renderDeleteWorkflowDoc(0)}
          </div>
        );
      }

      if (!canUpdate) return null;

      return (
        <Dialog>
          <DialogTrigger asChild>
            <Button
              className="hover:bg-primary hover:text-primary-foreground"
              data-cy="open-workflow-documentation-modal"
              variant="pushtextMuted"
            >
              <PlusCircle />
              Add documentation
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add documentation</DialogTitle>
            </DialogHeader>
            <UploadDocumentationModal workflowRowId={rowId} />
          </DialogContent>
        </Dialog>
      );
    };

    return (
      <tr
        className="border-spacing-1 border-border border-t pb-px text-sm hover:bg-muted [&>td]:px-4"
        data-cy={`workflow-row-element-${rowId}`}
        data-is-favorite={isFavorite}
      >
        <td className="group/wf w-1/2 min-w-[150px] max-w-0 overflow-hidden">
          <Link
            className="flex items-center gap-2 text-card-foreground no-underline"
            data-cy={`workflow-row-name-${rowId}`}
            to={pathToRegexp.compile(ROUTE_WORKFLOW_OVERVIEW)({ id: rowId })}
          >
            <Ellipsis className="py-[15px] capitalize">{name}</Ellipsis>
            <AddToFavorite
              className={cn(
                'opacity-100 delay-200 duration-100',
                !isFavorite &&
                  'opacity-0 group-hover/wf:opacity-100 group-hover/wf:transition-opacity'
              )}
              dataCy={`add-to-favorite-button-${rowId}`}
              isFavorite={isFavorite ?? false}
              onChange={value => onFavoriteChange(rowId, value)}
            />
          </Link>
        </td>
        <td className="whitespace-nowrap text-left" data-cy="trace-count">
          {formatNumber(traces.totalCount)}
        </td>
        <td className="whitespace-nowrap text-left" data-cy="group-count">
          {formatNumber(groups.totalCount)}
        </td>
        <td className="text-foreground text-xs" data-cy="documentation">
          {renderDocumentation()}
        </td>
        <td className="w-0">
          <div className="flex items-center gap-4">
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  aria-label="Click to manage notification subscriptions"
                  className="hover:text-primary"
                  data-cy="subscriptions-button"
                  disabled={subscriptionsDisabled}
                  size="icon"
                  title="Manage notification subscriptions"
                  variant="ghost"
                >
                  <BellRing />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <SubscriptionsMenu
                  actions={actions}
                  userId={userId}
                  workflowId={workflow.rowId}
                />
              </DialogContent>
            </Dialog>

            {env.FEATURE_FLAGS.USE_OLD_WF_OVERVIEW &&
              (ff_fastWorkflow || isPrivileged) && (
                <Link
                  className="block text-muted-foreground/50 hover:text-primary"
                  data-cy={`workflow-fast-overview-link-${rowId}`}
                  title="Go to the fast workflow overview"
                  to={pathToRegexp.compile(NEW_WF_OVERVIEW_ROUTE)({
                    id: rowId
                  })}
                >
                  <Zap size={18} />
                </Link>
              )}

            {canUpdate && isPrivileged && (
              <Link
                className="block text-muted-foreground hover:text-primary"
                data-cy={`workflow-settings-link-${rowId}`}
                title="Go to workflow settings"
                to={pathToRegexp.compile(ROUTE_WORKFLOW_GROUPS)({ id: rowId })}
              >
                <Settings size={18} />
              </Link>
            )}
          </div>
        </td>
      </tr>
    );
  }
);

WorkflowRow.propTypes = {
  workflow: PropTypes.object.isRequired,
  isPrivileged: PropTypes.bool,
  updateWorkflowDocumentation: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  isFavorite: PropTypes.bool.isRequired,
  onFavoriteChange: PropTypes.func.isRequired
};

export const fragments = {
  workflowRow: gql`
    fragment WorkflowRowFragment on Workflow {
      rowId
      canUpdate
      name
      description
      actions {
        nodes {
          key
          title
        }
      }
      settings
      # https://stratumn.atlassian.net/browse/ENG-248
      # https://github.com/stratumn/trace-api/pull/599
      # workflow.traces calls the workflow_traces() function, which seems to go through all workflow traces.

      # We just want the count in this case, and tracesDeprecated iterates on the raw table which is more optimized.
      # This reduces the user dashboard loading time from ~18s to ~5.5s for admin@stratumn.com on staging,
      # with a couple of workflows with thousands of traces.

      # Making the change in the backend is not possible because workflow.traces as it is
      # is used by cnp-proxy-server to get filtered traces.
      traces: tracesDeprecated {
        totalCount
      }
      groups {
        totalCount
      }
      documentation {
        url
        file {
          name
          size
          digest
        }
      }
    }
  `
};

export default compose(
  graphql(mutation.updateWorkflowDocumentation, {
    name: 'updateWorkflowDocumentation'
  })
)(WorkflowRow);
